import { AlpineData, LivewireData, LivewireMethods } from "@/js/interfaces/Components/alpinejs/Notification/NotificationIndex";
import { listen, sanitizeHtmlTag } from '@/js/app/support/utils';
import { BaseNotification, NotificationDto } from "@/js/interfaces/Components/alpinejs/NotificationMenu";
import { getBaseNotificationFromDto } from "@/js/app/services/NotificationAdapter";
import is from "@/js/app/support/is";
import { createAlpineLivewireComponent } from "@nolikein/typed-alpinejs";

/* Generated from the "artisan make:alpine" command */
export default {
    name: 'NotificationIndex',
    component: (
        // Component parameters...
        inputNotifications: Array<NotificationDto>,
        page: number,
        maxNotificationSize: number,
    ) => createAlpineLivewireComponent<AlpineData, LivewireData, LivewireMethods>({
        // Component properties ...
        notifications: [],
        loading: true,
        intervalId: null,
        page: page ?? 1,

        /**
         * Initialize the component
         */
        init() {
            this.pushNotificationsDto(inputNotifications);
            this.loading = false;

            // Every minute, update the created_at_for_humans silently
            this.intervalId = (setInterval(() => {
                if (this.notifications.length > 0) {
                    this.updateTimestampSilently();
                }
            }, 60 * 1000)) as unknown as number;

            listen('add-user-notification', (e) => {
                const data = <object>e.detail;

                // Check the id
                if (!('id' in data)) {
                    throw Error('The notification menu received an item without id');
                }
                if (!is.string(data.id)) {
                    throw Error('The notification menu received an item id that is not a string');
                }

                // Check the message
                if (!('message' in data)) {
                    throw Error('The notification menu received an item without message');
                }
                if (!is.string(data.message)) {
                    throw Error('The notification menu received an item message that is not a string');
                }

                // Check the created_at dispplayed "for humans"
                if (!('created_at_for_humans' in data)) {
                    throw Error('The notification menu received an item without created_at_for_humans');
                }
                if (!is.string(data.created_at_for_humans)) {
                    throw Error('The notification menu received an item created_at_for_humans that is not a string');
                }

                // Check the read_at
                if (!('read_at' in data)) {
                    throw Error('The notification menu received an item without read_at');
                }
                if (!is.string(data.read_at) && null !== data.read_at) {
                    throw Error('The notification menu received an item read_at that is not a string or null');
                }

                // Avoid having more than defined notification quantity in the same page
                if (this.notifications.length > maxNotificationSize) {
                    this.notifications.pop();
                }
                this.notifications.unshift(data as BaseNotification);
            });

            listen('pagination-changed', e => {
                const data = <object>e.detail;

                // Check the page property
                if (!('page' in data)) {
                    throw Error('The pagination page parameter should be present');
                }
                if (!is.number(data.page)) {
                    throw Error('The pagination page parameter should be a number');
                }

                this.notifications = [];
                this.loading = true;
                this.page = data.page;

                this.$wire.$call('getNotifications', data.page)
                    .then((notifications) => {
                        if (!is.array(notifications)) {
                            throw new Error('The notification data should be an array of notifications');
                        }

                        this.pushNotificationsDto(notifications);
                        this.loading = false;
                    });
            });

            listen('notifications-flush', () => {
                this.notifications = [];
            });
        },

        destroy() {
            if (null !== this.intervalId) {
                clearInterval(this.intervalId);
            }
        },

        updateTimestampSilently(): void {
            this.$wire.$call('getNotifications', this.page)
                .then((data) => {
                    if (!is.array(data)) {
                        throw new Error('The notification data should be an array of notifications');
                    }

                    data.forEach((notificationDto: NotificationDto) => {
                        this.notifications.filter(n => notificationDto.id === n.id)[0].created_at_for_humans = notificationDto.created_at_for_humans;
                    });
                });
        },

        /**
         * Checks if the notification list is empty
         */
        isNotificationListEmpty(): boolean {
            return 0 === this.notifications.length;
        },

        pushNotificationsDto(notifications: Array<NotificationDto>): void {
            notifications.forEach((notificationDto: NotificationDto) => {
                this.notifications.push(
                    getBaseNotificationFromDto(notificationDto),
                );
            });
        },

        getAlertMessage(alert: BaseNotification): string {
            // Clean message to prevent XSS. Use the sanitizeHtmlTag function in utils
            return sanitizeHtmlTag(alert.message, { allow: ['a'] });
        },
    }),
};
